import {
  PLAYER_LEVEL_MAX,
  certificationForItemLevel,
  type GuildRank,
} from '../progression/guild';

export type ConsumableRarity = 'common' | 'rare' | 'epic' | 'legendary';
export type ConsumableQualityStars = 1 | 2 | 3 | 4 | 5;
export type ConsumableResource = 'hp' | 'mp' | 'stamina';
export type ConsumableTraitKind = 'barrier' | 'regen' | 'cleanse' | 'status';

export interface ConsumableEffectRoll {
  kind: 'restore' | ConsumableTraitKind;
  resource?: ConsumableResource;
  flat?: number;
  pctMax?: number;
  value?: number;
  duration?: number;
  statusId?: string;
  count?: number;
}

export interface ConsumableTemplate {
  id: string;
  name: string;
  emoji: string;
  family: 'vital' | 'ether' | 'vigor';
  resource: ConsumableResource;
  allowedTraits?: ConsumableTraitKind[];
}

export interface ConsumableSource {
  kind: 'dungeon_monster' | 'dungeon_elite' | 'dungeon_master' | 'dungeon_chest' | 'shop' | 'legacy';
  dungeonId?: string;
  runId?: string;
  floorIndex?: number;
  receiptId?: string;
}

export interface ConsumableBatch {
  stackKey: string;
  templateId: string;
  itemLevel: number;
  rarity: ConsumableRarity;
  qualityStars: ConsumableQualityStars;
  effects: ConsumableEffectRoll[];
  requiredCertification: GuildRank;
  source: ConsumableSource;
  generatedAt: number;
}

export interface GenerateConsumableInput {
  template: ConsumableTemplate;
  itemLevel: number;
  rarity: ConsumableRarity;
  source: ConsumableSource;
  generatedAt?: number;
  rng?: () => number;
}

export const CONSUMABLE_TEMPLATES: Record<string, ConsumableTemplate> = {
  elixir_vital: {
    id: 'elixir_vital', name: 'Élixir vital', emoji: '🧪', family: 'vital', resource: 'hp',
    allowedTraits: ['regen', 'barrier', 'cleanse', 'status'],
  },
  ether_alchimique: {
    id: 'ether_alchimique', name: 'Éther alchimique', emoji: '💧', family: 'ether', resource: 'mp',
    // `regen` ouvert au mana (décision user 18/08) : il était le seul modèle à
    // en être privé, donc une potion de mana à effet prolongé était impossible
    // à produire — alors que la boutique doit désormais en garantir une.
    allowedTraits: ['regen', 'barrier', 'cleanse', 'status'],
  },
  tonique_martial: {
    id: 'tonique_martial', name: 'Tonique martial', emoji: '🌀', family: 'vigor', resource: 'stamina',
    allowedTraits: ['regen', 'barrier', 'cleanse', 'status'],
  },
};

export const CONSUMABLE_RARITY_EFFECT_COUNT: Record<ConsumableRarity, number> = {
  common: 1, rare: 2, epic: 3, legendary: 4,
};

const RARITY_CORE_MULTIPLIER: Record<ConsumableRarity, number> = {
  common: 1, rare: 1.05, epic: 1.1, legendary: 1.15,
};

const STAR_MULTIPLIER: Record<ConsumableQualityStars, number> = {
  1: 0.8, 2: 0.9, 3: 1, 4: 1.1, 5: 1.2,
};

const SUPPORT_STATUSES = ['defUp', 'vitUp', 'atkUp'] as const;
const CONSUMABLE_SOURCE_KINDS = new Set<ConsumableSource['kind']>([
  'dungeon_monster', 'dungeon_elite', 'dungeon_master', 'dungeon_chest', 'shop', 'legacy',
]);

function safeRandom(rng: () => number): number {
  const value = Number(rng());
  if (!Number.isFinite(value)) return 0;
  return Math.min(0.999999999, Math.max(0, value));
}

function drawQualityStars(rng: () => number): ConsumableQualityStars {
  const roll = safeRandom(rng);
  if (roll < 0.10) return 1;
  if (roll < 0.30) return 2;
  if (roll < 0.70) return 3;
  if (roll < 0.92) return 4;
  return 5;
}

function shuffledUnique<T>(values: readonly T[], count: number, rng: () => number): T[] {
  const copy = [...new Set(values)];
  for (let index = copy.length - 1; index > 0; index--) {
    const other = Math.floor(safeRandom(rng) * (index + 1));
    [copy[index], copy[other]] = [copy[other]!, copy[index]!];
  }
  return copy.slice(0, Math.max(0, count));
}

function restoreEffect(resource: ConsumableResource, itemLevel: number, rarity: ConsumableRarity, stars: ConsumableQualityStars): ConsumableEffectRoll {
  const star = STAR_MULTIPLIER[stars];
  const rarityMultiplier = RARITY_CORE_MULTIPLIER[rarity];
  const base = resource === 'hp' ? 35 + itemLevel * 1.9
    : resource === 'mp' ? 20 + itemLevel * 0.75
      : 20 + itemLevel * 0.55;
  return {
    kind: 'restore', resource,
    flat: Math.max(1, Math.round(base * star * rarityMultiplier)),
    pctMax: resource === 'hp' ? 0.08 : 0.07,
  };
}

function traitEffect(kind: ConsumableTraitKind, itemLevel: number, rarity: ConsumableRarity, stars: ConsumableQualityStars, rng: () => number): ConsumableEffectRoll {
  const star = STAR_MULTIPLIER[stars];
  const duration = rarity === 'legendary' ? 3 : 2;
  if (kind === 'barrier') return { kind, value: Math.round((20 + itemLevel * 0.9) * star), duration: 3 };
  if (kind === 'regen') return { kind, pctMax: rarity === 'legendary' ? 0.04 : rarity === 'epic' ? 0.035 : 0.03, duration };
  if (kind === 'cleanse') return { kind, count: rarity === 'legendary' ? 2 : 1 };
  return { kind: 'status', statusId: SUPPORT_STATUSES[Math.floor(safeRandom(rng) * SUPPORT_STATUSES.length)]!, duration };
}

function canonicalEffect(effect: ConsumableEffectRoll): ConsumableEffectRoll {
  const normalized: ConsumableEffectRoll = { kind: effect.kind };
  if (effect.resource !== undefined) normalized.resource = effect.resource;
  if (effect.flat !== undefined) normalized.flat = Number(effect.flat);
  if (effect.pctMax !== undefined) normalized.pctMax = Number(effect.pctMax);
  if (effect.value !== undefined) normalized.value = Number(effect.value);
  if (effect.duration !== undefined) normalized.duration = Number(effect.duration);
  if (effect.statusId !== undefined) normalized.statusId = String(effect.statusId);
  if (effect.count !== undefined) normalized.count = Number(effect.count);
  return normalized;
}

export function consumableMechanicalIdentity(batch: Pick<ConsumableBatch,
  'templateId' | 'itemLevel' | 'rarity' | 'qualityStars' | 'effects' | 'requiredCertification'>) {
  return {
    templateId: String(batch.templateId),
    itemLevel: Number(batch.itemLevel),
    rarity: batch.rarity,
    qualityStars: Number(batch.qualityStars),
    effects: (batch.effects || []).map(canonicalEffect),
    requiredCertification: batch.requiredCertification,
  };
}

/** Encodage base64url injectif : la clé contient toute l'identité mécanique. */
function base64UrlUtf8(value: string): string {
  const bytes = new TextEncoder().encode(value);
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  let output = '';
  for (let index = 0; index < bytes.length; index += 3) {
    const a = bytes[index]!;
    const hasB = index + 1 < bytes.length;
    const hasC = index + 2 < bytes.length;
    const b = hasB ? bytes[index + 1]! : 0;
    const c = hasC ? bytes[index + 2]! : 0;
    output += alphabet[a >>> 2];
    output += alphabet[((a & 3) << 4) | (b >>> 4)];
    if (hasB) output += alphabet[((b & 15) << 2) | (c >>> 6)];
    if (hasC) output += alphabet[c & 63];
  }
  return output;
}

export function consumableStackKey(batch: Pick<ConsumableBatch,
  'templateId' | 'itemLevel' | 'rarity' | 'qualityStars' | 'effects' | 'requiredCertification'>): string {
  const identity = consumableMechanicalIdentity(batch);
  return `con_${identity.templateId}_v2_${base64UrlUtf8(JSON.stringify(identity))}`;
}

/** Valide un lot persiste puis reconstruit sa forme canonique. */
export function normalizeConsumableBatch(raw: any): ConsumableBatch | null {
  if (!raw || typeof raw !== 'object') return null;
  const template = CONSUMABLE_TEMPLATES[String(raw.templateId || '')];
  const itemLevel = Number(raw.itemLevel);
  const rarity = raw.rarity as ConsumableRarity;
  const qualityStars = Number(raw.qualityStars) as ConsumableQualityStars;
  if (!template || !Number.isInteger(itemLevel) || itemLevel < 1 || itemLevel > PLAYER_LEVEL_MAX) return null;
  if (!(rarity in CONSUMABLE_RARITY_EFFECT_COUNT) || ![1, 2, 3, 4, 5].includes(qualityStars)) return null;
  if (!Array.isArray(raw.effects) || raw.effects.length !== CONSUMABLE_RARITY_EFFECT_COUNT[rarity]) return null;
  if (raw.requiredCertification !== certificationForItemLevel(itemLevel)) return null;

  const expectedRestore = restoreEffect(template.resource, itemLevel, rarity, qualityStars);
  const restore = canonicalEffect(raw.effects[0]);
  if (JSON.stringify(restore) !== JSON.stringify(expectedRestore)) return null;

  const duration = rarity === 'legendary' ? 3 : 2;
  const seenTraits = new Set<string>();
  const traits: ConsumableEffectRoll[] = [];
  for (const candidate of raw.effects.slice(1)) {
    const effect = canonicalEffect(candidate);
    if (!template.allowedTraits?.includes(effect.kind as ConsumableTraitKind) || seenTraits.has(effect.kind)) return null;
    seenTraits.add(effect.kind);
    if (effect.kind === 'barrier') {
      const expected = { kind: 'barrier' as const, value: Math.round((20 + itemLevel * 0.9) * STAR_MULTIPLIER[qualityStars]), duration: 3 };
      if (JSON.stringify(effect) !== JSON.stringify(expected)) return null;
      traits.push(expected);
    } else if (effect.kind === 'regen') {
      const expected = { kind: 'regen' as const, pctMax: rarity === 'legendary' ? 0.04 : rarity === 'epic' ? 0.035 : 0.03, duration };
      if (JSON.stringify(effect) !== JSON.stringify(expected)) return null;
      traits.push(expected);
    } else if (effect.kind === 'cleanse') {
      const expected = { kind: 'cleanse' as const, count: rarity === 'legendary' ? 2 : 1 };
      if (JSON.stringify(effect) !== JSON.stringify(expected)) return null;
      traits.push(expected);
    } else if (effect.kind === 'status') {
      if (!SUPPORT_STATUSES.includes(effect.statusId as typeof SUPPORT_STATUSES[number]) || effect.duration !== duration
        || Object.keys(effect).some((key) => !['kind', 'statusId', 'duration'].includes(key))) return null;
      traits.push({ kind: 'status', statusId: effect.statusId, duration });
    } else return null;
  }

  const sourceKind = CONSUMABLE_SOURCE_KINDS.has(raw.source?.kind) ? raw.source.kind : 'legacy';
  const source: ConsumableSource = { kind: sourceKind };
  for (const key of ['dungeonId', 'runId', 'receiptId'] as const) {
    if (typeof raw.source?.[key] === 'string' && raw.source[key]) source[key] = raw.source[key];
  }
  if (Number.isInteger(raw.source?.floorIndex)) source.floorIndex = raw.source.floorIndex;
  const generatedAt = Number.isFinite(Number(raw.generatedAt)) ? Number(raw.generatedAt) : 0;
  const identity = {
    templateId: template.id, itemLevel, rarity, qualityStars,
    effects: [expectedRestore, ...traits], requiredCertification: certificationForItemLevel(itemLevel),
  };
  return { ...identity, stackKey: consumableStackKey(identity), source, generatedAt };
}

export function generateConsumableBatch(input: GenerateConsumableInput): ConsumableBatch {
  if (!input.template?.id) throw new Error('modèle de consommable requis');
  const rng = input.rng || Math.random;
  const itemLevel = Math.max(1, Math.min(PLAYER_LEVEL_MAX, Math.floor(Number(input.itemLevel) || 1)));
  const qualityStars = drawQualityStars(rng);
  const traitCount = Math.max(0, CONSUMABLE_RARITY_EFFECT_COUNT[input.rarity] - 1);
  const allowed = input.template.allowedTraits || [];
  const traits = shuffledUnique(allowed, Math.min(traitCount, allowed.length), rng);
  const effects = [
    restoreEffect(input.template.resource, itemLevel, input.rarity, qualityStars),
    ...traits.map((trait) => traitEffect(trait, itemLevel, input.rarity, qualityStars, rng)),
  ];
  const identity = { templateId: input.template.id, itemLevel, rarity: input.rarity, qualityStars, effects, requiredCertification: certificationForItemLevel(itemLevel) };
  return {
    ...identity,
    stackKey: consumableStackKey(identity),
    source: { ...input.source },
    generatedAt: input.generatedAt ?? Date.now(),
  };
}

/** RNG déterministe partagé avec les reçus de drop serveur. */
export function createSeededConsumableRng(seed: string): () => number {
  let state = 2166136261;
  for (let index = 0; index < seed.length; index++) {
    state ^= seed.charCodeAt(index);
    state = Math.imul(state, 16777619);
  }
  return () => {
    state += 0x6D2B79F5;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

export function consumableRestoreAmount(effect: ConsumableEffectRoll, target: any): number {
  if (effect.kind !== 'restore') return 0;
  const maximum = effect.resource === 'hp' ? Number(target?.maxHp || 0)
    : effect.resource === 'mp' ? Number(target?.maxMp || 0)
      : Number(target?.maxStamina || 0);
  return Math.max(0, Math.floor(Number(effect.flat || 0) + maximum * Number(effect.pctMax || 0)));
}
